import type {
  ExecutionDetail,
  ExecutionHandle,
  ExecutionState,
  ExecutionSummary,
  TerminalExecutionState,
} from "./types";

export interface ExecutionSnapshotState {
  detail: ExecutionDetail | null;
  handle: ExecutionHandle;
  summary: ExecutionSummary | null;
}

const EXECUTION_KIND_LABELS: Record<ExecutionHandle["kind"], string> = {
  bridge: "Bridge",
  deposit: "Deposit",
  execution: "Execution",
  rebalance: "Rebalance",
  swap: "Swap",
  transfer: "Transfer",
  withdraw: "Withdraw",
};

const EXECUTION_STATE_LABELS: Record<ExecutionState, string> = {
  accepted: "Queued",
  awaiting_user: "Action required",
  cancelled: "Cancelled",
  completed: "Completed",
  failed: "Failed",
  retrying: "Retrying",
  running: "Running",
};

export function isTerminalExecutionState(
  state: ExecutionState | null | undefined,
): state is TerminalExecutionState {
  return state === "cancelled" || state === "completed" || state === "failed";
}

export function getExecutionKindLabel(kind: ExecutionHandle["kind"]): string {
  return EXECUTION_KIND_LABELS[kind];
}

export function formatExecutionStateLabel(state: ExecutionState): string {
  return EXECUTION_STATE_LABELS[state];
}

export function getExecutionTone(
  state: ExecutionState,
): "default" | "error" | "success" | "warning" {
  if (state === "completed") {
    return "success";
  }

  if (state === "failed") {
    return "error";
  }

  if (state === "cancelled" || state === "awaiting_user") {
    return "warning";
  }

  return "default";
}

export function buildExecutionHeadline(
  handle: ExecutionHandle,
  summary: ExecutionSummary | null,
): string {
  if (summary?.headline) {
    return summary.headline;
  }

  return `${getExecutionKindLabel(handle.kind)} ${formatExecutionStateLabel(handle.state)}`;
}

export function buildExecutionDetailText(
  handle: ExecutionHandle,
  summary: ExecutionSummary | null,
): string | null {
  if (summary?.detail) {
    return summary.detail;
  }

  if (summary?.terminalReason) {
    return summary.terminalReason;
  }

  if (handle.error) {
    return handle.error;
  }

  const phase = summary?.phase ?? handle.progress?.phase;
  if (phase) {
    return phase;
  }

  const stepIndex = summary?.stepIndex ?? handle.progress?.stepIndex;
  const totalSteps = summary?.totalSteps ?? handle.progress?.totalSteps;
  if (
    typeof stepIndex === "number" &&
    Number.isFinite(stepIndex) &&
    typeof totalSteps === "number" &&
    Number.isFinite(totalSteps)
  ) {
    return `Step ${stepIndex} of ${totalSteps}`;
  }

  return null;
}

export function applyExecutionDetail(
  detail: ExecutionDetail,
): ExecutionSnapshotState {
  return {
    detail,
    handle: detail.operation,
    summary: detail.summary,
  };
}
